import React from 'react';
import { useNavigate } from 'react-router-dom';

import PrimaryButton from '../../../components/Buttons/PrimaryButton';
import { CheckmarkIcon } from '../../../assets/svg/SVGComponents';
import { formatWallClockTime } from '../../../utils/scheduleDate';
import {
  Container,
  HeaderContent,
  MainCard,
  ScoreSummary,
  ScorecardBonusPill,
  ScorecardInfoCell,
  ScorecardInfoGrid,
  ScorecardMembersSection,
  ScorecardMissionItem,
  ScorecardMissionList,
  ScorecardNavRow,
} from './styles';

import { textSize } from '../../../styles/brand';
export interface IScorecard {
  id: string;
  round_number: number;
  status: 'scored';
  slot: {
    id: string;
    starts_at: string;
    ends_at: string;
    arena_name: string;
  };
  team: {
    id: string;
    name: string;
    category: string;
    modality_slug: string;
    members: { student_name: string; school_year: number }[];
  };
  judge: { id: string; name: string } | null;
  modality: {
    slug: string;
    name: string;
    evaluation_type: 'subjective' | 'binary';
    time_limit_seconds: number;
    bonus_time_seconds: number;
    bonus_points: number;
    penalty_per_occurrence: number;
    mission_count: number;
  };
  scoring: {
    mission_scores: { mission_order: number; score: number }[];
    mission_sum: number;
    completion_time_seconds: number | null;
    infraction_count: number;
    touch_count: number;
    penalty_deduction: number;
    bonus_applied: boolean;
    bonus_time_threshold: number;
    bonus_points_earned: number;
    total_score: number;
  };
}

interface IScorecardViewProps {
  scorecard: IScorecard;
  roundNumber: number;
  maxRounds: number;
  tournamentId: string;
}

const MISSION_NAMES: Record<string, string[]> = {
  robo_pintor: [
    'Era Medieval (Parábola)',
    '1ª Rev. Industrial (Retas)',
    '2ª Rev. Industrial (Zig-Zag)',
    '3ª Rev. Industrial (Quadrado)',
    'Indústria 4.0 (Círculo)',
  ],
  seguidor_de_linha: [
    'A Ponte Mecânica',
    'A Engrenagem Perdida',
    'Transmitindo Energia',
    'Elevador Automatizado',
    'A Leitura dos Símbolos',
  ],
};

const CATEGORY_LABELS: Record<string, string> = {
  category_1: 'Categoria 1',
  category_2: 'Categoria 2',
  category_3: 'Categoria 3',
  category_4: 'Categoria 4',
};

// Score per mission is 20 pts for both modalities
const MISSION_MAX_SCORE = 20;

export const ScorecardView: React.FC<IScorecardViewProps> = ({
  scorecard,
  roundNumber,
  maxRounds,
  tournamentId,
}) => {
  const navigate = useNavigate();
  const { team, judge, slot, modality, scoring } = scorecard;

  const missionNames = MISSION_NAMES[modality.slug] ?? [];
  const sortedMissions = [...scoring.mission_scores].sort(
    (a, b) => a.mission_order - b.mission_order,
  );

  const occurrenceCount =
    modality.evaluation_type === 'binary'
      ? scoring.touch_count
      : scoring.infraction_count;
  const occurrenceLabel =
    modality.evaluation_type === 'binary' ? 'Toques' : 'Infrações';

  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  };

  return (
    <Container>
      <HeaderContent>
        <div
          style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem' }}
        >
          <h1>
            Resultado · Rodada {roundNumber}/{maxRounds} · {team.name}
          </h1>
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.3rem',
              fontSize: textSize(0.78),
              fontWeight: 600,
              color: '#16a34a',
              background: '#dcfce7',
              border: '1px solid #bbf7d0',
              borderRadius: '20px',
              padding: '0.15rem 0.65rem',
              alignSelf: 'flex-start',
            }}
          >
            <CheckmarkIcon
              aria-hidden
              width="0.95rem"
              stroke="currentColor"
              strokeWidth={2}
              fill="none"
            />
            Avaliação concluída · somente leitura
          </span>
        </div>
      </HeaderContent>

      <MainCard>
        <ScorecardInfoGrid>
          <ScorecardInfoCell>
            <span className="label">Arena</span>
            <span className="value">{slot.arena_name}</span>
          </ScorecardInfoCell>
          <ScorecardInfoCell>
            <span className="label">Horário</span>
            <span className="value">
              {formatWallClockTime(slot.starts_at)} –{' '}
              {formatWallClockTime(slot.ends_at)}
            </span>
          </ScorecardInfoCell>
          <ScorecardInfoCell>
            <span className="label">Juiz</span>
            <span className="value">{judge?.name ?? '—'}</span>
          </ScorecardInfoCell>
        </ScorecardInfoGrid>

        <ScorecardMissionList>
          <h3
            style={{
              fontSize: textSize(1),
              fontWeight: 600,
              color: '#333',
              marginBottom: '0.25rem',
            }}
          >
            Missões — {modality.name} ·{' '}
            {CATEGORY_LABELS[team.category] ?? team.category}
          </h3>
          {sortedMissions.map((ms, idx) => (
            <ScorecardMissionItem
              key={ms.mission_order}
              $score={ms.score}
              $maxScore={MISSION_MAX_SCORE}
            >
              <span className="name">
                {missionNames[idx] ?? `Missão ${ms.mission_order}`}
              </span>
              <span className="score">{ms.score} pts</span>
            </ScorecardMissionItem>
          ))}
        </ScorecardMissionList>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '1rem',
            marginTop: '0.75rem',
          }}
        >
          <ScorecardBonusPill $applied={scoring.bonus_applied}>
            {scoring.bonus_applied && (
              <CheckmarkIcon
                aria-hidden
                width="1rem"
                stroke="currentColor"
                strokeWidth={2}
                fill="none"
              />
            )}
            {scoring.bonus_applied
              ? `Bônus de tempo aplicado (+${scoring.bonus_points_earned} pts)`
              : 'Sem bônus de tempo'}
          </ScorecardBonusPill>

          {scoring.completion_time_seconds !== null && (
            <span style={{ fontSize: textSize(0.85), color: '#6b7280' }}>
              Tempo: {formatTime(scoring.completion_time_seconds)}
            </span>
          )}
        </div>

        {occurrenceCount > 0 && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '0.75rem 1rem',
              border: '1px solid #ffcccc',
              borderRadius: '8px',
              background: '#fffafa',
              marginTop: '0.5rem',
            }}
          >
            <span style={{ fontSize: textSize(0.95), color: '#333' }}>
              {occurrenceLabel} (–{modality.penalty_per_occurrence} pts cada)
            </span>
            <span
              style={{
                fontSize: textSize(1.1),
                fontWeight: 700,
                color: '#dc3545',
              }}
            >
              {occurrenceCount}
            </span>
          </div>
        )}

        <ScoreSummary>
          <div className="box">
            <span className="label">Soma das missões</span>
            <span className="value">{scoring.mission_sum} pts</span>
          </div>
          <div className="box">
            <span className="label">Bônus</span>
            <span className="value bonus">
              +{scoring.bonus_points_earned} pts
            </span>
          </div>
          <div className="box">
            <span className="label">Penalidades</span>
            <span className="value penalty">
              –{scoring.penalty_deduction} pts
            </span>
          </div>
          <div className="box">
            <span className="label">Total</span>
            <span className="value total">{scoring.total_score} pts</span>
          </div>
        </ScoreSummary>

        {team.members.length > 0 && (
          <ScorecardMembersSection>
            <h4>Integrantes da equipe</h4>
            <ul>
              {team.members.map(m => (
                <li key={m.student_name}>
                  {m.student_name}{' '}
                  <span style={{ color: '#9ca3af', fontSize: textSize(0.8) }}>
                    ({m.school_year}º ano)
                  </span>
                </li>
              ))}
            </ul>
          </ScorecardMembersSection>
        )}
      </MainCard>

      <ScorecardNavRow>
        <PrimaryButton
          type="button"
          onClick={() => navigate(`/tournament/${tournamentId}`)}
        >
          Voltar ao torneio
        </PrimaryButton>
      </ScorecardNavRow>
    </Container>
  );
};
